import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  FAILURES,
  ICaptureTimestamp,
  IImageFormat,
  IImageQuality,
  IPrimaryDocumentImages,
  PrimaryDocumentCaptureStep,
} from "../../../types/onboard";
import Instruction from "../common/Instruction";
import DocumentAutoCapture from "./DocumentAutoCapture";
import { DocumentCallback } from "@innovatrics/dot-document-auto-capture/.";
import { IDocumentUploadedResponse, IUploadDocumentRequestBody } from "../../../types/api/application.type";
import { BlobToBase64, getUserDevice, primaryDocumentOptions } from "../../../utils/helper";
import Failure from "../common/Failure";
import Success from "../common/Success";
import { handlePrimaryDocumentPhotoTaken } from "../../../actions/onboard";
import commonStyles from "../../../styles/common.module.css";
import { PrimaryDocumentCategories, PrimaryDocumentErrorCodes } from "../../../types/document";
import { useApiError } from "../../../provider/ApiErrorProvider";
import Error from "../../common/Error";
import { useClientSession } from "../../../provider/ClientSessionProvider";
import { useClient } from "../../../provider/ClientProvider";
import BarcodeFailure from "../common/BarcodeFailure";
import { useTranslation } from "react-i18next";
import { ControlEventInstruction, dispatchControlEvent, DocumentCustomEvent } from "@innovatrics/dot-document-auto-capture/events";
interface Props {
  photoUrl: string | undefined;
  setPhotoUrl: Dispatch<SetStateAction<string | undefined>>;
  id: string | undefined;
  setImageFormat: Dispatch<SetStateAction<IImageFormat>>;
  setCaptureTimeStamp: Dispatch<SetStateAction<ICaptureTimestamp>>;
  setImageQuality: Dispatch<SetStateAction<IImageQuality>>
  handleBackClick: () => void;
  handleNextOnBoardStep: () => Promise<void>;
  handleOnBoardBackStep: () => void;
  handleError: (error: Error) => void;
  primaryDocumentImages:IPrimaryDocumentImages;
  setPrimaryDocumentImages:React.Dispatch<React.SetStateAction<IPrimaryDocumentImages>>
}

const PrimaryDocumentCapture = ({
  id,
  setPhotoUrl,
  handleBackClick,
  handleNextOnBoardStep,
  handleOnBoardBackStep,
  handleError,
  setCaptureTimeStamp,
  setImageFormat,
  setImageQuality,
  photoUrl,
  primaryDocumentImages,
setPrimaryDocumentImages
}: Props) => {
  const {setError,errorMessage} = useApiError()
  const { endSession } = useClientSession();
  // IDV-4: the picker offers exactly what the partner accepts. The same list is
  // enforced server-side, so this is presentation, not the control.
  const { client } = useClient();
  const documentOptions =
    client?.acceptedDocuments.map((d) => ({ value: d.documentKey, label: d.name })) ??
    primaryDocumentOptions;
  const [currentStep, setCurrentStep] = useState(1);
  const [selected, setSelected] = useState("");
  const [documentIndex, setDocumentIndex] = useState(0);
  const [documentUploadedResponse, setDocumentUploadedResponse] = useState<IDocumentUploadedResponse>({
    errorCode:"",
    errorMessage:""
  });
  const [documentReqBody, setDocumentReqbody] =
    useState<IUploadDocumentRequestBody>({
      image: {
        imageData: "",
        captureTimestamp: "",
        format: "JPEG",
      },
      imageType:"front"
    });

  const handleNextStep = async () => {
    setPhotoUrl(undefined);
    setCurrentStep((prevStep) => Math.min(prevStep + 1, 4));
  };

  const handlePrimaryDocumentCaptureStep = async (stepNumber: number) => {
    setPhotoUrl(undefined);
    setCurrentStep(stepNumber);
  };

  useEffect(() => {
    sessionStorage.setItem("PD_Type", selected);
  }, [selected]);

  const DocumentPhotoTaken: DocumentCallback = (imageData, content) => {
    const imageUrl = URL.createObjectURL(imageData.image);
    const capturedTimeStamp = Date.now();
    const isFront = currentStep === PrimaryDocumentCaptureStep.FRONT_CAPTURE;
    const format = imageData?.image?.type?.split("/")[1]?.toUpperCase();
    const imageQuality = imageData?.data?.detection?.sharpness * 100;
    setPhotoUrl(imageUrl);
    setImageFormat((prev) => ({
      ...prev,
      [isFront ? "documentFront" : "documentBack"]: format,
    })); 
    setCaptureTimeStamp((prev) => ({
      ...prev,
      [isFront ? "documentFront" : "documentBack"]: capturedTimeStamp,
    }));
    setImageQuality((prev) => ({
      ...prev,
      [isFront ? "documentFront" : "documentBack"]: imageQuality,
    }));

    BlobToBase64(imageData.image, (base64String) => {
      if (!base64String) return;
      const base64Image = base64String as string;

      const updatedBody = singleSidedDocuments.includes(selected) ?  
      {
        ...documentReqBody, // Ensure we get the latest state
        image: {
          ...documentReqBody.image,
          imageData: base64Image.split(",")[1],
          captureTimestamp: capturedTimeStamp.toString(),
          format,
        },
        imageType: "front"
      }
      :
      {
        ...documentReqBody, // Ensure we get the latest state
        ["image"]: {
          ...documentReqBody["image"],
          imageData: base64Image.split(",")[1],
          captureTimestamp: capturedTimeStamp.toString(),
          format,
        },
        ["imageType"]: isFront ? "front" : "back"
      };

      const updatedPrimaryDocumentImages = singleSidedDocuments.includes(selected) ?  
      {
        ...primaryDocumentImages,
        frontImage: base64Image.split(",")[1],
        backImage: ""
      }
      :
      {
        ...primaryDocumentImages, 
        [isFront ? "frontImage" : "backImage"]: base64Image.split(",")[1]
      };

      setDocumentReqbody(updatedBody);
      setPrimaryDocumentImages(updatedPrimaryDocumentImages);
        handlePrimaryDocumentPhotoTaken(
          id,
          updatedBody,
          setDocumentIndex,
          handlePrimaryDocumentCaptureStep,
          setDocumentUploadedResponse,
          setError,
          singleSidedDocuments.includes(selected),
          handleContinueDetection
        );
      });
  };

  const isDocumentUnacceptable =
    documentUploadedResponse?.errorCode ===
    PrimaryDocumentErrorCodes.DOCUMENT_NOT_ACCEPTED;

  function getFailureBody(errorCode:string) {
    switch(errorCode){
    case PrimaryDocumentErrorCodes.DOCUMENT_NOT_SUPPORTED:
      return t('primarydocument.documentintegrity.support.tip')
    case PrimaryDocumentErrorCodes.DOCUMENT_NOT_ACCEPTED:
      return t('primarydocument.documentintegrity.accepted.tip')
    // Reached on mobile, where the barcode failure falls through to the ordinary
    // failure screen. That screen already offered a way forward; the tip it showed
    // was the generic capture advice, which says nothing about the barcode.
    case PrimaryDocumentErrorCodes.DOCUMENT_BARCODE_NOT_DETECTED:
      return t('primarydocument.documentintegrity.barcode.tip')
    case PrimaryDocumentErrorCodes.DOCUMENT_IS_A_SCREENSHOT:
      return t("primarydocument.documentintegrity.screenshot.tip");
    case PrimaryDocumentErrorCodes.DOCUMENT_NOT_RECOGNIZED:
      return t("primarydocument.documentintegrity.recognition.tip");
    case PrimaryDocumentErrorCodes.DOCUMENT_HAS_EXPIRED:
      return t("primarydocument.documentintegrity.expired.tip");
    case PrimaryDocumentErrorCodes.DOCUMENT_PAGES_DOES_NOT_MATCH:
      return t("primarydocument.documentintegrity.match.tip");
    case PrimaryDocumentErrorCodes.CARD_CORNERS_NOT_DETECTED:
      return t("primarydocument.documentintegrity.corners.tip");
    default:
      return t("primarydocument.documentintegrity.default.tip")
    }
  }


  const singleSidedDocuments = [PrimaryDocumentCategories.PASSPORT.toString()];

    const handleContinueDetection = () => {
    setPhotoUrl(undefined);
    dispatchControlEvent(
      DocumentCustomEvent.CONTROL,
      ControlEventInstruction.CONTINUE_DETECTION
    );
    handlePrimaryDocumentCaptureStep(
      PrimaryDocumentCaptureStep.BACK_CAPTURE
    );
  };

  const {t} = useTranslation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  }, [currentStep]);

  const renderStep = (currentStep: PrimaryDocumentCaptureStep) => {
    switch (currentStep) {
      case PrimaryDocumentCaptureStep.INSTRUCTION:
        return (
          <Instruction
            header={t('primarydocument.title')}
            description={`${t('primarydocument.description1')} ${getUserDevice() === "MOBILE" ? t('mobile') : t('computer') } ${t('primarydocument.description2')} 
`}
            tips={[
              t('primarydocument.tip1'),
              `${getUserDevice() === "MOBILE" ? t('primarydocument.tip2.mobile') :
                t('primarydocument.tip2')
              }`,
              t('primarydocument.tip3'),
              t('primarydocument.tip4'),
            ]}
            images={["/assets/document-front-generic.svg", "/assets/document-back-generic.svg"]}
            buttonLabel={t('button.continue')}
            onClick={handleNextStep}
            isSelectRequired={true}
            selected={selected}
            setSelected={setSelected}
            options={documentOptions}
            selectLabel={t('primarydocument.select.placeholder')}
            tipHeader={
              <span className={commonStyles.primaryTipHeader}>
                {t('primarydocument.tip.header')}
              </span>
            }
          />
        );
      case PrimaryDocumentCaptureStep.FRONT_CAPTURE:
        return (
          <DocumentAutoCapture
            selectedDocumentType={selected}
            onNext={handleNextStep}
            onPhotoTaken={DocumentPhotoTaken}
            onError={handleError}
            photoUrl={photoUrl}
            documentCaptureSide={"FRONT_SIDE"}
            setPhotoUrl={setPhotoUrl}
          />
        );
      case PrimaryDocumentCaptureStep.BACK_CAPTURE:
        return (
          <DocumentAutoCapture
            selectedDocumentType={selected}
            onNext={handleNextStep}
            onPhotoTaken={DocumentPhotoTaken}
            onError={handleError}
            photoUrl={photoUrl}
            documentCaptureSide={"BACK_SIDE"}
            setPhotoUrl={setPhotoUrl}
          />
        );
      case PrimaryDocumentCaptureStep.ON_SUCCESS:
        return (
          <Success
            header={t('primarydocument.success.header')}
            body={
              <span className={commonStyles.description}>
                {t('success.body')}<strong>{t('success.continue')}</strong>{" "}{t('primarydocument.success.nextstep')}
              </span>
            }
            onClick={handleNextOnBoardStep}
            buttonLabel={t('button.continue')}
          />
        );
      case PrimaryDocumentCaptureStep.ON_FAILURE:
        /*
         * A document the issuer does not accept is a dead end for this document,
         * so the two ordinary ways out both mislead: "Retake" implies a better
         * photo might be accepted, and "Continue with the existing capture" would
         * submit something the issuer has already refused. Send them back to the
         * picker instead, and offer nothing else.
         */
        return isDocumentUnacceptable ? (
          <Failure
            header={t(FAILURES.PRIMARY_DOCUMENT_CAPTURE)}
            description={documentUploadedResponse?.errorMessage}
            body={getFailureBody(documentUploadedResponse?.errorCode) || ""}
            onClick={() => {
              setSelected("");
              setDocumentIndex(0);
              setPrimaryDocumentImages({ frontImage: "", backImage: "" });
              handlePrimaryDocumentCaptureStep(PrimaryDocumentCaptureStep.INSTRUCTION);
            }}
            buttonLabel={t('button.differentdocument')}
            onContinue={() => {}}
            continueButtonRequired={false}
          />
        ) : (
          <Failure
            header={t(FAILURES.PRIMARY_DOCUMENT_CAPTURE)}
            description={documentUploadedResponse?.errorMessage}
            body={getFailureBody(documentUploadedResponse?.errorCode) || ""}
            onClick={() => {
              sessionStorage.setItem("retake", "true");
              if(documentIndex == 0){
                handlePrimaryDocumentCaptureStep(PrimaryDocumentCaptureStep.FRONT_CAPTURE)
              }else{
                handlePrimaryDocumentCaptureStep(PrimaryDocumentCaptureStep.BACK_CAPTURE);
              }
            }}
            buttonLabel={t('button.retake')}
            onContinue={() =>{
              if(documentIndex == 0 && !singleSidedDocuments.includes(selected)){
                handleContinueDetection();
              }else{
                handlePrimaryDocumentCaptureStep(
                  PrimaryDocumentCaptureStep.ON_SUCCESS
                )
              }
            }
             
            }
          />
        );
      case PrimaryDocumentCaptureStep.ON_BARCODE_DETECTION_FAILURE:
        return (
          <BarcodeFailure 
            onExit={() => {
              sessionStorage.setItem("showQRPopup","true");
              endSession()}}
            onRetry={() => {
              sessionStorage.setItem("showQRPopup","false");
              sessionStorage.setItem("retake", "true");
              handlePrimaryDocumentCaptureStep(PrimaryDocumentCaptureStep.BACK_CAPTURE);
            }}
            /*
              The barcode is only ever checked on the back upload, so this lands on
              ON_SUCCESS in practice. Mirrors the ordinary failure screen's branch
              anyway rather than assuming the index.
            */
            onContinue={() => {
              sessionStorage.setItem("showQRPopup","false");
              if(documentIndex == 0 && !singleSidedDocuments.includes(selected)){
                handleContinueDetection();
              }else{
                handlePrimaryDocumentCaptureStep(PrimaryDocumentCaptureStep.ON_SUCCESS);
              }
            }}
            header={t(FAILURES.PRIMARY_DOCUMENT_CAPTURE)}
            description={t('primarydocument.documentintegrity.barcodedetection')}
            body={t('primarydocument.documentintegrity.barcode.tip')}
          />
        )
    }
  };
  return <>
  {
    errorMessage != null ? 
    <Error errorMessage={errorMessage}/>
    :
    renderStep(currentStep)
  }
    </>;
};

export default PrimaryDocumentCapture;
